import React, { useState, useMemo } from 'react';
import { Typography, Button, Box } from '@mui/material';

/**
 * ReadMoreText - A reusable component for displaying text with "Read more/Show less" functionality
 * 
 * @component
 * @example
 * // Basic usage
 * <ReadMoreText 
 *   text="This is a very long text that will be truncated..."
 *   maxLength={100}
 * />
 * 
 * @example
 * // Custom styling
 * <ReadMoreText 
 *   text={longDescription}
 *   maxLength={200}
 *   variant="body1"
 *   color="text.primary"
 *   sx={{ mt: 2 }}
 *   buttonSx={{ color: 'secondary.main' }}
 *   readMoreText="Continue reading"
 *   showLessText="Show less"
 * />
 * 
 * @param {Object} props - Component props
 * @param {string} props.text - The text to display (required)
 * @param {number} [props.maxLength=200] - Maximum characters to show before truncating
 * @param {string} [props.variant="body2"] - Typography variant
 * @param {string} [props.color="text.secondary"] - Text color
 * @param {Object} [props.sx={}] - Additional styles for the container Box
 * @param {Object} [props.buttonSx={}] - Additional styles for the button
 * @param {string} [props.readMoreText="Read more"] - Text for the "read more" button
 * @param {string} [props.showLessText="Show less"] - Text for the "show less" button
 */
const ReadMoreText = ({ 
    text, 
    maxLength = 200, 
    variant = "body2", 
    color = "text.secondary",
    sx = {},
    buttonSx = {},
    readMoreText = "Read more",
    showLessText = "Show less"
}) => {
    const [showFullText, setShowFullText] = useState(false);

    const { displayText, isLongText, shouldShowButton } = useMemo(() => {
        if (!text) {
            return { displayText: '', isLongText: false, shouldShowButton: false };
        }

        const isLongText = text.length > maxLength;
        const shouldShowButton = isLongText;
        
        let displayText = text;
        if (isLongText && !showFullText) {
            displayText = text.substring(0, maxLength) + '...';
        }

        return { displayText, isLongText, shouldShowButton };
    }, [text, maxLength, showFullText]);

    const handleToggle = (e) => {
        e.stopPropagation();
        setShowFullText(!showFullText);
    };

    const defaultButtonSx = {
        ml: 1, 
        p: 0, 
        minWidth: 0, 
        fontSize: 13, 
        color: 'primary.main', 
        textTransform: 'none', 
        background: 'none', 
        boxShadow: 'none', 
        border: 'none', 
        '&:hover': { 
            background: 'none', 
            textDecoration: 'underline' 
        }
    };

    return (
        <Box sx={sx}>
            <Typography variant={variant} color={color}>
                {displayText}
                {shouldShowButton && (
                    <Button
                        variant="text"
                        size="small"
                        sx={{ ...defaultButtonSx, ...buttonSx }}
                        onClick={handleToggle}
                    >
                        {showFullText ? showLessText : readMoreText}
                    </Button>
                )}
            </Typography>
        </Box>
    );
};

export default ReadMoreText; 